import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { frontOfficeApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { 
  BedDouble, User, Sparkles, AlertTriangle, Wrench, Clock,
  CheckCircle, XCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function RoomStatusPage() {
  const { currentProperty } = useAuth();
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState('all');

  const fetchRoomStatus = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const response = await frontOfficeApi.getRoomStatus(
        currentProperty.id, 
        selectedFloor !== 'all' ? selectedFloor : undefined
      );
      setRoomData(response.data);
    } catch (error) {
      toast.error('Failed to load room status');
    } finally {
      setLoading(false);
    }
  }, [currentProperty, selectedFloor]);

  useEffect(() => {
    fetchRoomStatus();
    // Refresh every 60 seconds
    const interval = setInterval(fetchRoomStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchRoomStatus]);

  const handleUpdateStatus = async (newStatus, notes, reason, expectedReturn) => {
    if (!selectedRoom) return;
    try {
      await frontOfficeApi.updateRoomStatus(selectedRoom.id, {
        maintenance_status: newStatus,
        notes,
        ooo_reason: reason,
        ooo_expected_return: expectedReturn,
      });
      toast.success('Room status updated');
      setStatusDialogOpen(false);
      setSelectedRoom(null);
      fetchRoomStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const getRoomStatusColor = (room) => {
    const ms = room.maintenance_status || room.display_status;
    
    if (ms === 'OUT_OF_ORDER') return 'bg-red-500 text-white';
    if (ms === 'OUT_OF_SERVICE') return 'bg-gray-400 text-white';
    if (ms === 'AVAILABLE') return 'bg-green-500 text-white';
    return 'bg-gray-200';
  };

  const getRoomStatusIcon = (room) => {
    const ms = room.maintenance_status || room.display_status;
    
    if (ms === 'OUT_OF_ORDER') return <Wrench className="h-4 w-4" />;
    if (ms === 'OUT_OF_SERVICE') return <AlertTriangle className="h-4 w-4" />;
    if (ms === 'AVAILABLE') return <Sparkles className="h-4 w-4" />;
    return <BedDouble className="h-4 w-4" />;
  };

  const floors = roomData?.floors
    ? Object.keys(roomData.floors).filter((f) => f && f.trim() !== '').sort()
    : [];
  const floorLabel = (floor) => (floor === 'Unassigned' ? 'Unassigned' : `Floor ${floor}`);

  return (
    <div className="space-y-6" data-testid="room-status-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Room Status Board</h1>
          <p className="text-muted-foreground">Visual room status for {currentProperty?.name}</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedFloor} onValueChange={setSelectedFloor}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Floors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Floors</SelectItem>
              {floors.map((floor) => (
                <SelectItem key={floor} value={floor}>{floorLabel(floor)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchRoomStatus}>Refresh</Button>
        </div>
      </div>

      {/* Summary Cards */}
      {roomData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold">{roomData.summary.total}</div>
                <div className="text-sm text-muted-foreground">Total Rooms</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-700">{roomData.summary.vacant_clean}</div>
                <div className="text-sm text-green-600">Vacant Clean</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-700">{roomData.summary.vacant_dirty}</div>
                <div className="text-sm text-amber-600">Vacant Dirty</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-700">{roomData.summary.occupied}</div>
                <div className="text-sm text-blue-600">Occupied</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-700">{roomData.summary.out_of_order}</div>
                <div className="text-sm text-red-600">Out of Order</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500" />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500" />
          <span>Out of Order</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gray-400" />
          <span>Out of Service</span>
        </div>
      </div>

      {/* Room Grid by Floor */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading room status...
          </CardContent>
        </Card>
      ) : !roomData?.floors || floors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground" data-testid="no-rooms">
            <BedDouble className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No rooms found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {floors.map((floor) => (
            <Card key={floor}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{floorLabel(floor)}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                  {roomData.floors[floor].map((room) => (
                    <div
                      key={room.id}
                      className={cn(
                        "p-3 rounded-lg cursor-pointer transition-all hover:scale-105 hover:shadow-lg",
                        getRoomStatusColor(room)
                      )}
                      onClick={() => {
                        setSelectedRoom(room);
                        setStatusDialogOpen(true);
                      }}
                      data-testid={`room-${room.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold">{room.room_number}</span>
                        {getRoomStatusIcon(room)}
                      </div>
                      <div className="text-xs opacity-80">{room.room_types?.code || room.room_types?.name}</div>
                      <div className="text-[10px] mt-1 font-medium opacity-90">
                        {room.maintenance_status === 'OUT_OF_ORDER' ? 'Out of Order' : 
                         room.maintenance_status === 'OUT_OF_SERVICE' ? 'Out of Service' : 'Available'}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Room Status</DialogTitle>
            <DialogDescription>Room #{selectedRoom?.room_number} - {selectedRoom?.room_types?.name}</DialogDescription>
          </DialogHeader>
          {selectedRoom && (
            <RoomStatusForm 
              room={selectedRoom} 
              onSubmit={handleUpdateStatus}
              onCancel={() => setStatusDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoomStatusForm({ room, onSubmit, onCancel }) {
  const [status, setStatus] = useState(room.maintenance_status || 'AVAILABLE');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState(room.ooo_reason || '');
  const [expectedReturn, setExpectedReturn] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(status, notes, reason, expectedReturn || null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="room-status-form">
      <div className="p-4 bg-muted rounded-lg">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Current Status:</span>
            <span className="ml-2 font-medium">{room.maintenance_status || 'AVAILABLE'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Room Type:</span>
            <span className="ml-2 font-medium">{room.room_types?.name || '-'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>New Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="AVAILABLE">Available</SelectItem>
            <SelectItem value="OUT_OF_ORDER">Out of Order</SelectItem>
            <SelectItem value="OUT_OF_SERVICE">Out of Service</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(status === 'OUT_OF_ORDER' || status === 'OUT_OF_SERVICE') && (
        <>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              placeholder="e.g., Plumbing issue"
            />
          </div>
          <div className="space-y-2">
            <Label>Expected Return Date</Label>
            <Input
              type="date"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value)}
            />
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes..."
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="update-status-btn">Update Status</Button>
      </div>
    </form>
  );
}
